// components/ProtectedRoute/index.tsx
'use client';
import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useUserSession } from '../../lib/context/UserSessionContext';

const IS_BROWSER = typeof window !== 'undefined';

function canUseLocalStorage(): boolean {
    try {
        const ls = window.localStorage;
        return typeof ls?.setItem === 'function';
    } catch {
        return false;
    }
}

interface ProtectedRouteProps {
    children: React.ReactNode;
    requiredRole?: 'designer' | 'supplier';
    redirectTo?: string;
    loadingScreen?: React.ReactNode;
}

export default function ProtectedRoute({
    children,
    requiredRole,
    redirectTo = '/login',
    loadingScreen = <p>Loading...</p>,
}: ProtectedRouteProps) {
    const { sessionInfo, isLoadingSession } = useUserSession();
    const router = useRouter();

    useEffect(() => {
        if (isLoadingSession) return;

        if (!sessionInfo?.isSessionValid) {
            // Unauthenticated: save current path and redirect to login
            if (IS_BROWSER && canUseLocalStorage()) {
                // Save the full browser path (including /ai if present) for external/root consumers
                localStorage.setItem('loginFrom', window.location.pathname + window.location.search);
                // Use window.location.replace to bypass Next.js basePath "attaching" logic
                window.location.replace(redirectTo);
            }
            return;
        }

        if (requiredRole && sessionInfo.userType !== requiredRole) {
            // Authenticated but wrong role: redirect to unauthorized
            // For unauthorized, we can stay within the app/basePath
            router.replace('/unauthorized');
        }
    }, [isLoadingSession, sessionInfo, requiredRole, redirectTo, router]);

    // Show loader while session is being fetched
    if (isLoadingSession) return <>{loadingScreen}</>;

    // Block render until we know the user is authorized
    if (!sessionInfo?.isSessionValid) return <>{loadingScreen}</>;

    if (requiredRole && sessionInfo.userType !== requiredRole) return <>{loadingScreen}</>;

    return <>{children}</>;
}
